import React, { useCallback, useMemo, useState } from 'react';
import { useBillingStatsRelayQuery } from '../../hooks/transaction/query/V2/useBillingStatsRelay';
import {
  mapPeriodStatsToDayChartData,
  mapPeriodStatsToMonthChartData,
  mapBucketsToChartData,
  getDistributionLabels,
  mapQuickStats,
  mapDistributionStats,
} from '../../hooks/transaction/query/V2/billingStatsMappers';
import { convertCentsToMoney, formatNumberWithCommas } from '../../utils';
import Controls from '../dashboard/assets/controls';
import { monthArray } from '../../constants';
import { ISystem } from '../../ts/interface/system.interface';
import { connect } from 'react-redux';
import DashboardChart from '../dashboard/assets/dashboard-chart';
import Stack from '@mui/material/Stack';
import Card from '@mui/material/Card';
import QuickStats from '../../components/Redesing/quick-stats';
import { Paper } from '@mui/material';
import CardLoader from '../../components/Redesing/card-loader';
import BillingDashboardChart from './assets/billing-dashboard-chart';
import { useSystemStore } from '../../store/systemStore';
const timeZones = require('../../../data/timezones.json');

const DEFAULT_YEAR_RANGE = 10;

const Dashboard = ({
  system,
  systemLoaded,
}: {
  system: ISystem;
  systemLoaded: boolean;
}) => {
  const systemGid = useSystemStore((state) => state.systemInfo.gid);

  const [currentMonth, setCurrentMonth] = useState(new Date().getUTCMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [timePeriod, setTimePeriod] = useState<string>('month');

  // Generate a fixed year array for the Controls component
  const yearArray = useMemo(() => {
    const now = new Date().getFullYear();
    const years: number[] = [];
    for (let y = now; y >= now - DEFAULT_YEAR_RANGE; y--) {
      years.push(y);
    }
    return years;
  }, []);

  // Compute date range for the selected period
  const { startDate, endDate } = useMemo(() => {
    if (timePeriod === 'month') {
      const start = new Date(Date.UTC(currentYear, currentMonth, 1));
      const end = new Date(Date.UTC(currentYear, currentMonth + 1, 0, 23, 59, 59));
      return {
        startDate: start.toISOString(),
        endDate: end.toISOString(),
      };
    } else {
      const start = new Date(Date.UTC(currentYear, 0, 1));
      const end = new Date(Date.UTC(currentYear, 11, 31, 23, 59, 59));
      return {
        startDate: start.toISOString(),
        endDate: end.toISOString(),
      };
    }
  }, [timePeriod, currentMonth, currentYear]);

  // Single query for both quick stats and charts
  const { data: billingStats, isLoading } = useBillingStatsRelayQuery({
    systemId: systemGid,
    granularity: timePeriod === 'month' ? 'DAY' : 'MONTH',
    startDate,
    endDate,
    enabled: systemLoaded && !!systemGid,
  });

  // Quick stats (same period as charts)
  const quickStats = useMemo(() => mapQuickStats(billingStats), [billingStats]);

  // Charges chart data
  const chargesChartData = useMemo(() => {
    if (timePeriod === 'month') {
      return mapPeriodStatsToDayChartData(
        billingStats?.chargesByPeriod ?? null,
        currentMonth,
        currentYear
      );
    }
    return mapPeriodStatsToMonthChartData(
      billingStats?.chargesByPeriod ?? null,
      currentYear
    );
  }, [billingStats, timePeriod, currentMonth, currentYear]);

  // Wallet debits chart data
  const walletDebitsChartData = useMemo(() => {
    if (timePeriod === 'month') {
      return mapPeriodStatsToDayChartData(
        billingStats?.walletDebitsByPeriod ?? null,
        currentMonth,
        currentYear
      );
    }
    return mapPeriodStatsToMonthChartData(
      billingStats?.walletDebitsByPeriod ?? null,
      currentYear
    );
  }, [billingStats, timePeriod, currentMonth, currentYear]);

  // Refunds chart data
  const refundsChartData = useMemo(() => {
    if (timePeriod === 'month') {
      return mapPeriodStatsToDayChartData(
        billingStats?.refundsByPeriod ?? null,
        currentMonth,
        currentYear
      );
    }
    return mapPeriodStatsToMonthChartData(
      billingStats?.refundsByPeriod ?? null,
      currentYear
    );
  }, [billingStats, timePeriod, currentMonth, currentYear]);

  // Distribution chart data
  const distributionChartData = useMemo(
    () => mapBucketsToChartData(billingStats?.chargeDistribution ?? null),
    [billingStats]
  );

  const distributionLabels = useMemo(
    () => getDistributionLabels(billingStats?.chargeDistribution ?? null),
    [billingStats]
  );

  const distributionStats = useMemo(
    () => mapDistributionStats(billingStats),
    [billingStats]
  );

  const hasWalletDebits = (billingStats?.walletDebitsByPeriod?.length ?? 0) > 0;

  // Handle Controls component callbacks
  const updatePeriod = useCallback(
    (key: string, period: string | number, _updateSte: boolean, obj: any) => {
      const yearIdx = Number(obj.currentYear);
      const year = yearArray[yearIdx] ?? new Date().getFullYear();
      const month = Number(obj.currentMonth);

      setCurrentYear(year);
      setCurrentMonth(month);

      if (key === 'timePeriod') {
        setTimePeriod(String(period));
      }
    },
    [yearArray]
  );

  const { timezone } = system;
  const systemTimezoneLabel = timeZones.find((zone: { utc: string[] }) =>
    zone.utc.find((name: string) => name === timezone)
  )?.text;

  // Monthly tick values for day charts
  const monthTickValues = useMemo(() => {
    if (timePeriod === 'month' && 'tickValues' in chargesChartData) {
      return (chargesChartData as any).tickValues as number[];
    }
    return [];
  }, [chargesChartData, timePeriod]);

  const dateRangeLabel = useMemo(() => {
    if (timePeriod === 'month') {
      return `${monthArray[currentMonth]} ${currentYear}`;
    }
    return `${currentYear}`;
  }, [timePeriod, currentMonth, currentYear]);

  const content = () => {
    return (
      <Stack spacing={2}>
        <QuickStats
          sections={[
            {
              title: 'Average Charge',
              subtitle: dateRangeLabel,
              value: `$${quickStats.averageCharge}`,
            },
            {
              title: 'Total Charges',
              subtitle: dateRangeLabel,
              value: formatNumberWithCommas(quickStats.chargeCount),
            },
            {
              title: 'Average Refund',
              subtitle: dateRangeLabel,
              value: `$${quickStats.averageRefund}`,
            },
            {
              title: 'Total Refunds',
              subtitle: dateRangeLabel,
              value: formatNumberWithCommas(quickStats.refundCount),
            },
          ]}
          isLoading={isLoading}
        />
        <Paper sx={{ borderRadius: '20px' }}>
          <Stack pl={2} pt={2}>
            <Controls
              timePeriod={timePeriod}
              yearArray={yearArray}
              currentMonth={currentMonth}
              updateTimePeriod={updatePeriod}
              fromBilling
            />
          </Stack>
          {isLoading ? (
            <CardLoader useCard />
          ) : (
            <>
              {timePeriod === 'month' ? (
                <Stack>
                  <Stack pt={2} px={2}>
                    <Card>
                      <DashboardChart
                        id={'charge'}
                        data={{
                          data: chargesChartData.data as any[],
                          labels: monthTickValues as any[],
                        }}
                        total={convertCentsToMoney(chargesChartData.total)}
                        average={convertCentsToMoney(chargesChartData.average)}
                        systemTimezoneLabel={systemTimezoneLabel}
                        title={'Card Charges by day'}
                        statTittleOne={'Average by day'}
                        statTittleTwo={'Total'}
                      />
                    </Card>
                  </Stack>
                  {hasWalletDebits && (
                    <Stack pt={2} px={2}>
                      <Card>
                        <DashboardChart
                          id={'wallet_debits'}
                          data={{
                            data: walletDebitsChartData.data as any[],
                            labels: monthTickValues as any[],
                          }}
                          total={convertCentsToMoney(walletDebitsChartData.total)}
                          average={convertCentsToMoney(walletDebitsChartData.average)}
                          systemTimezoneLabel={systemTimezoneLabel}
                          title={'Wallet Charges by day'}
                          statTittleOne={'Average by day'}
                          statTittleTwo={'Total'}
                        />
                      </Card>
                    </Stack>
                  )}
                  <Stack pt={2} px={2} pb={2}>
                    <Card>
                      <DashboardChart
                        id={'refund_by_day'}
                        data={{
                          data: refundsChartData.data as any[],
                          labels: monthTickValues as any[],
                        }}
                        total={convertCentsToMoney(refundsChartData.total)}
                        average={convertCentsToMoney(refundsChartData.average)}
                        systemTimezoneLabel={systemTimezoneLabel}
                        title={'Refunds by Day'}
                        statTittleOne={'Average by day'}
                        statTittleTwo={'Total'}
                      />
                    </Card>
                  </Stack>
                  <Stack pt={2} px={2}>
                    <Card>
                      <DashboardChart
                        id={'rentals_by_charge'}
                        data={{
                          data: distributionChartData as any[],
                          labels: distributionLabels as string[],
                        }}
                        total={formatNumberWithCommas(distributionStats.totalRentals)}
                        average={convertCentsToMoney(distributionStats.rentalAverage)}
                        systemTimezoneLabel={systemTimezoneLabel}
                        title={'Rentals by Charge'}
                        statTittleOne={'Average Charge'}
                        statTittleTwo={'Total Rentals'}
                      />
                    </Card>
                  </Stack>
                </Stack>
              ) : (
                // YEARLY GRAPHS
                <Stack>
                  <Stack pt={2} px={2}>
                    <Card>
                      <BillingDashboardChart
                        id={'charges_by_month'}
                        data={{
                          data: chargesChartData.data as any[],
                          labels: monthArray as string[],
                        }}
                        total={convertCentsToMoney(chargesChartData.total)}
                        average={convertCentsToMoney(chargesChartData.average)}
                        systemTimezoneLabel={systemTimezoneLabel}
                        title={'Card Charges by Month'}
                        statTittleOne={'Average by month'}
                        statTittleTwo={'Total'}
                      />
                    </Card>
                  </Stack>
                  {hasWalletDebits && (
                    <Stack pt={2} px={2}>
                      <Card>
                        <BillingDashboardChart
                          id={'wallet_debits_by_month'}
                          data={{
                            data: walletDebitsChartData.data as any[],
                            labels: monthArray as string[],
                          }}
                          total={convertCentsToMoney(walletDebitsChartData.total)}
                          average={convertCentsToMoney(walletDebitsChartData.average)}
                          systemTimezoneLabel={systemTimezoneLabel}
                          title={'Wallet Charges by Month'}
                          statTittleOne={'Average by month'}
                          statTittleTwo={'Total'}
                        />
                      </Card>
                    </Stack>
                  )}
                  <Stack pt={2} px={2} pb={2}>
                    <Card>
                      <BillingDashboardChart
                        id={'refunds_by_month'}
                        data={{
                          data: refundsChartData.data as any[],
                          labels: monthArray as string[],
                        }}
                        total={convertCentsToMoney(refundsChartData.total)}
                        average={convertCentsToMoney(refundsChartData.average)}
                        systemTimezoneLabel={systemTimezoneLabel}
                        title={'Refunds by Month'}
                        statTittleOne={'Average by month'}
                        statTittleTwo={'Total'}
                      />
                    </Card>
                  </Stack>
                  <Stack pt={2} px={2}>
                    <Card>
                      <BillingDashboardChart
                        id={'rentals_by_charge_yearly'}
                        data={{
                          data: distributionChartData as any[],
                          labels: distributionLabels as string[],
                        }}
                        total={formatNumberWithCommas(distributionStats.totalRentals)}
                        average={convertCentsToMoney(distributionStats.rentalAverage)}
                        systemTimezoneLabel={systemTimezoneLabel}
                        title={'Rentals by Charge'}
                        statTittleOne={'Average Charge'}
                        statTittleTwo={'Total Rentals'}
                      />
                    </Card>
                  </Stack>
                </Stack>
              )}
            </>
          )}
        </Paper>
      </Stack>
    );
  };

  return <>{content()}</>;
};

export default connect(
  (state: { system: { current: ISystem; isLoaded: boolean } }) => ({
    system: state.system.current,
    systemLoaded: state.system.isLoaded,
  }),
  () => ({})
)(Dashboard);
